"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./AnalyticsScrollingSection.module.css";

const FEATURES = [
  {
    id: "engagement",
    title: "Engagement tracking",
    description:
      "See exactly where viewers drop off, rewatch, or skip ahead in your video.",
  },
  {
    id: "geography",
    title: "Geographic breakdown",
    description:
      "Who's watching, by country and city. Play rate and engagement per region.",
  },
  {
    id: "overview",
    title: "Heatmaps",
    description:
      "Visualize viewer activity day by day. Spot patterns in when and how they watch.",
  },
  {
    id: "sessions",
    title: "Activity overview",
    description:
      "Zoom in on a single session to see exactly how that viewer interacted with the video.",
  },
];

/* ──────────────────────────────────────────────────────────────────────
   Session activity list — a list of individual viewer sessions with the
   percentage each watched. Clicking a session expands a HubSpot-style
   activity log of how that viewer interacted with the video (paused,
   resumed, skipped, with time-gap separators between visits).
   ────────────────────────────────────────────────────────────────────── */

// Each session's `events` are listed in chronological order (oldest first);
// the log renders them reversed so the most recent action sits on top.
const SESSIONS = [
  {
    id: "s1",
    location: "Berlin",
    device: "Desktop",
    browser: "Chrome",
    watched: 94,
    events: [
      { kind: "event", icon: "start", label: "Started watching", time: "0:00", detail: "Entered from a LinkedIn link" },
      { kind: "event", icon: "pause", label: "Paused", time: "2:18", detail: "Idle for 6 seconds" },
      { kind: "event", icon: "left", label: "Left the page", time: "2:24", detail: "Closed the tab on /pricing" },
      { kind: "gap", label: "3 days ago" },
      { kind: "event", icon: "resume", label: "Resumed playback", time: "2:18", detail: "Picked up right where they left off" },
      { kind: "event", icon: "skip", label: "Skipped ahead", time: "4:45", detail: "Jumped past the pricing section" },
      { kind: "event", icon: "finish", label: "Watched to the end", time: "6:02", detail: "Completed 94% of the video" },
    ],
  },
  {
    id: "s2",
    location: "London",
    device: "Desktop",
    browser: "Firefox",
    watched: 88,
    events: [
      { kind: "event", icon: "start", label: "Started watching", time: "0:00", detail: "Direct visit" },
      { kind: "event", icon: "finish", label: "Watched straight through", time: "5:18", detail: "No pauses or skips" },
    ],
  },
  {
    id: "s3",
    location: "Austin",
    device: "Mobile",
    browser: "Safari",
    watched: 61,
    events: [
      { kind: "event", icon: "start", label: "Started watching", time: "0:00", detail: "Opened on mobile data" },
      { kind: "event", icon: "rewatch", label: "Rewatched intro", time: "0:34", detail: "Replayed the first 30 seconds" },
      { kind: "event", icon: "pause", label: "Paused", time: "3:41", detail: "Switched tabs" },
      { kind: "gap", label: "4 hours ago" },
      { kind: "event", icon: "resume", label: "Resumed playback", time: "3:41", detail: "Returned on the same device" },
      { kind: "event", icon: "dropoff", label: "Dropped off", time: "3:58", detail: "Left after 17 seconds" },
    ],
  },
  {
    id: "s4",
    location: "Tokyo",
    device: "Tablet",
    browser: "Safari",
    watched: 38,
    events: [
      { kind: "event", icon: "start", label: "Started watching", time: "0:00", detail: "Entered from an email campaign" },
      { kind: "event", icon: "skip", label: "Skipped ahead", time: "1:50", detail: "Scrubbed forward twice" },
      { kind: "event", icon: "dropoff", label: "Dropped off", time: "2:18", detail: "Stopped playback" },
      { kind: "event", icon: "left", label: "Left the page", time: "2:22", detail: "Navigated to the homepage" },
    ],
  },
];

function LogIcon({ name }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "start":
    case "resume":
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <path d="M8 5.14v13.72c0 .79.87 1.27 1.54.85l10.84-6.86a1 1 0 0 0 0-1.7L9.54 4.29A1 1 0 0 0 8 5.14z" />
        </svg>
      );
    case "pause":
      return (
        <svg {...common} fill="currentColor" stroke="none">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
      );
    case "skip":
      return (
        <svg {...common}>
          <polyline points="13 5 20 12 13 19" />
          <polyline points="5 5 12 12 5 19" />
        </svg>
      );
    case "rewatch":
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <polyline points="3 4 3 10 9 10" />
        </svg>
      );
    case "left":
      return (
        <svg {...common}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      );
    case "dropoff":
      return (
        <svg {...common}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case "finish":
    default:
      return (
        <svg {...common}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
  }
}

function SessionActivityList() {
  const [animated, setAnimated] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const openLogRef = useRef(null);

  // Highest watch-rate first.
  const sessions = useMemo(
    () => [...SESSIONS].sort((a, b) => b.watched - a.watched),
    []
  );

  useEffect(() => {
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, []);

  // Auto-expand the top session a beat after the rows finish settling so
  // visitors see the drill-down interaction without having to click.
  useEffect(() => {
    if (!animated) return undefined;
    const timer = setTimeout(() => {
      setExpandedId((prev) => (prev == null ? sessions[0].id : prev));
    }, 900);
    return () => clearTimeout(timer);
  }, [animated, sessions]);

  // Smoothly grow the activity log open. Closing is handled in CSS — the log
  // wrapper's `grid-template-rows` animates 1fr → 0fr in a plain block context,
  // which collapses smoothly. But while open the wrapper is flex-filled (a
  // definite height), and a single `fr` grid track in a definite container
  // snaps straight to full size instead of animating. So on open we drive the
  // height explicitly here, then hand back to the flex layout for scrolling.
  useLayoutEffect(() => {
    if (expandedId == null) return undefined;
    const el = openLogRef.current;
    if (!el || typeof el.animate !== "function") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    const target = el.offsetHeight; // flex-filled height = the panel space it fills
    if (!target) return undefined;

    const inner = el.firstElementChild;
    el.style.flex = "none"; // let the explicit height drive the reveal
    if (inner) inner.style.overflowY = "hidden"; // no scrollbar flicker mid-grow

    const reveal = el.animate(
      [{ height: "0px" }, { height: `${target}px` }],
      { duration: 450, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    );
    const restore = () => {
      el.style.flex = "";
      if (inner) inner.style.overflowY = "";
    };
    reveal.addEventListener("finish", restore);
    reveal.addEventListener("cancel", restore);

    return () => reveal.cancel();
  }, [expandedId]);

  return (
    <div className={styles.sessionsWrap}>
      <div className={styles.sessionsHeader}>
        <span className={styles.sessionsTitle}>Recent sessions</span>
        <span className={styles.sessionsMeta}>{sessions.length} viewers</span>
      </div>

      <ul className={`${styles.sessionsList} ${expandedId ? styles.sessionsListExpanded : ""}`}>
        {sessions.map((session, idx) => {
          const isOpen = expandedId === session.id;
          const reversed = [...session.events].reverse();
          return (
            <li
              key={session.id}
              className={`${styles.sessionItem} ${animated ? styles.sessionItemVisible : ""} ${isOpen ? styles.sessionItemOpen : ""}`}
              style={{ transitionDelay: `${idx * 55}ms` }}
            >
              <button
                type="button"
                className={styles.sessionRow}
                onClick={() => setExpandedId((prev) => (prev === session.id ? null : session.id))}
                aria-expanded={isOpen}
              >
                {/* Row background = watch-rate progress, left → right */}
                <span
                  className={styles.sessionFill}
                  style={{ width: animated ? `${session.watched}%` : "0%" }}
                  aria-hidden="true"
                />
                <span className={styles.sessionInfo}>
                  <span className={styles.sessionName}>
                    <span className={styles.sessionLocation}>{session.location}</span>
                    <span className={styles.sessionMeta}> · {session.device} · {session.browser}</span>
                  </span>
                </span>
                <span className={styles.sessionRight}>
                  <span className={styles.sessionPct}>{session.watched}%</span>
                  <svg
                    className={`${styles.sessionChevron} ${isOpen ? styles.sessionChevronOpen : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>

              <div
                className={styles.sessionLogWrap}
                ref={isOpen ? openLogRef : undefined}
              >
                <div className={styles.sessionLogInner}>
                  <ol className={styles.sessionLog}>
                    {reversed.map((event, i) =>
                      event.kind === "gap" ? (
                        <li key={i} className={styles.logGap}>
                          <span className={styles.logGapLine} aria-hidden="true" />
                          <span className={styles.logGapLabel}>{event.label}</span>
                          <span className={styles.logGapLine} aria-hidden="true" />
                        </li>
                      ) : (
                        <li key={i} className={styles.logEvent}>
                          <span className={styles.logIcon} aria-hidden="true">
                            <LogIcon name={event.icon} />
                          </span>
                          <span className={styles.logEventBody}>
                            <span className={styles.logEventTop}>
                              <span className={styles.logLabel}>{event.label}</span>
                              {event.time && <span className={styles.logTime}>{event.time}</span>}
                            </span>
                            {event.detail && (
                              <span className={styles.logDetail}>{event.detail}</span>
                            )}
                          </span>
                        </li>
                      )
                    )}
                  </ol>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Engagement curve — orange retention-style curve with a smooth
   monotone-cubic shape and animated draw-in. Mirrors the path math
   in EmbedAnalyticsSection's RetentionPanel.
   ────────────────────────────────────────────────────────────────────── */

// Smooth exponential-decay drop-off from 100 → ~28%, matching the deeper
// curve in the Tella reference. The displayed "average viewed" is an
// aggregate metric, not the curve's endpoint.
const ENGAGEMENT_COUNTS = [
  100, 86, 74, 64, 56, 49, 44, 40, 37, 35, 33, 32, 31, 30, 30, 29, 29, 29, 29, 28,
];
const ENGAGEMENT_AVERAGE = 73;

function buildEngagementPaths({ counts, svgWidth, svgHeight, leftPadding, rightPadding, topPadding, bottomPadding }) {
  const max = Math.max(1, ...counts);
  const plotBottom = svgHeight - bottomPadding;
  const plotHeight = plotBottom - topPadding;
  const xStep = counts.length > 1 ? (svgWidth - leftPadding - rightPadding) / (counts.length - 1) : 0;

  const points = counts.map((count, idx) => {
    const x = leftPadding + idx * xStep;
    const norm = max === 0 ? 0 : count / max;
    const y = topPadding + (1 - norm) * plotHeight;
    return { x, y };
  });

  const n = points.length;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);

  const ms = new Array(Math.max(0, n - 1));
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1] - xs[i];
    ms[i] = dx !== 0 ? (ys[i + 1] - ys[i]) / dx : 0;
  }

  const ts = new Array(n);
  ts[0] = ms[0] || 0;
  ts[n - 1] = ms[n - 2] || 0;
  for (let i = 1; i < n - 1; i++) {
    const m0 = ms[i - 1];
    const m1 = ms[i];
    if (m0 === 0 || m1 === 0 || (m0 > 0 && m1 < 0) || (m0 < 0 && m1 > 0)) ts[i] = 0;
    else ts[i] = (m0 + m1) / 2;
  }

  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 0; i < n - 1; i++) {
    const h = xs[i + 1] - xs[i];
    const c1x = xs[i] + h / 3;
    const c1y = ys[i] + (ts[i] * h) / 3;
    const c2x = xs[i + 1] - h / 3;
    const c2y = ys[i + 1] - (ts[i + 1] * h) / 3;
    d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${xs[i + 1]} ${ys[i + 1]}`;
  }

  const last = { x: xs[n - 1], y: ys[n - 1] };
  const first = { x: xs[0], y: ys[0] };
  const areaPath = `${d} L ${last.x} ${plotBottom} L ${first.x} ${plotBottom} Z`;
  return { linePath: d, areaPath };
}

/* ──────────────────────────────────────────────────────────────────────
   Geography list — ranked list of countries with magnitude bars,
   mirroring the analytics modal's TopLocations panel. Stagger-fades
   in on mount; remounts (and replays) when the user clicks back to
   the geography tab thanks to the `key` prop in the parent JSX.
   ────────────────────────────────────────────────────────────────────── */

const GEOGRAPHY_COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸", views: 2974 },
  { code: "DE", name: "Germany", flag: "🇩🇪", views: 1858 },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧", views: 1043 },
  { code: "FR", name: "France", flag: "🇫🇷", views: 672 },
  // Brazil is shown on desktop only — on mobile the panel is shorter (16/12,
  // no CLS) and fits 4 rows, so the lowest-ranked country is dropped there.
  { code: "BR", name: "Brazil", flag: "🇧🇷", views: 559 },
];

function GeographyList() {
  const [animated, setAnimated] = useState(false);
  // Mobile (≤768px) uses the shorter no-CLS panel that fits 4 rows, so we drop
  // the lowest-ranked country (Brazil) there; desktop shows all of them.
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const allSorted = [...GEOGRAPHY_COUNTRIES].sort((a, b) => b.views - a.views);
  const sorted = isMobile ? allSorted.slice(0, 4) : allSorted;
  const total = sorted.reduce((sum, c) => sum + c.views, 0);
  const max = Math.max(1, ...sorted.map((c) => c.views));

  return (
    <div className={styles.geographyWrap}>
      <div className={styles.geographyHeader}>
        <span className={styles.geographyTitle}>Top locations</span>
        <span className={styles.geographyCount}>{sorted.length} countries</span>
      </div>

      <ul className={styles.geographyList}>
        {sorted.map((country, idx) => {
          const barWidth = (country.views / max) * 100;
          const sharePct = Math.round((country.views / total) * 100);
          return (
            <li
              key={country.code}
              className={`${styles.geographyRow} ${animated ? styles.geographyRowVisible : ""}`}
              style={{ transitionDelay: `${idx * 60}ms` }}
            >
              <span
                className={styles.geographyBar}
                style={{
                  width: animated ? `${barWidth}%` : "0%",
                  transitionDelay: `${idx * 60 + 100}ms`,
                }}
                aria-hidden="true"
              />
              <span className={styles.geographyLeft}>
                <span className={styles.geographyFlag}>{country.flag}</span>
                <span className={styles.geographyName}>{country.name}</span>
              </span>
              <span className={styles.geographyRight}>
                <span className={styles.geographyValue}>{country.views.toLocaleString()}</span>
                <span className={styles.geographyShare}>{sharePct}%</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Heatmap grid — calendar-style retention intensity by day. Distinct
   from the geography list (rows of text + bars) and the engagement
   curve (single line). Each cell is one day in a 7-row × 14-col grid,
   colored by retention level on a 5-step orange scale.
   ────────────────────────────────────────────────────────────────────── */

// Intensity levels 0–4; mapped to demo view counts for hover tooltips.
const HEATMAP_INTENSITY = [
  [1, 2, 1, 3, 4, 2, 3, 4, 3, 2, 1, 3, 4, 2],
  [2, 3, 2, 4, 3, 1, 2, 3, 4, 3, 2, 4, 3, 1],
  [3, 4, 3, 4, 3, 2, 4, 3, 4, 3, 2, 3, 4, 2],
  [2, 3, 4, 3, 4, 3, 2, 4, 3, 4, 3, 4, 3, 1],
  [3, 4, 3, 2, 4, 3, 4, 3, 2, 3, 4, 3, 2, 0],
  [1, 2, 1, 0, 1, 2, 1, 0, 1, 2, 1, 0, 1, 0],
  [0, 1, 2, 1, 0, 1, 0, 1, 2, 1, 0, 1, 2, 1],
];

const HEATMAP_INTENSITY_VIEWS = [0, 3, 6, 10, 17];

const HEATMAP_DATA = HEATMAP_INTENSITY.map((row) =>
  row.map((level) => HEATMAP_INTENSITY_VIEWS[level] ?? 0)
);

const HEATMAP_DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

const HEATMAP_COLORS = [
  "rgba(255, 255, 255, 0.04)",
  "rgba(255, 103, 2, 0.2)",
  "rgba(255, 103, 2, 0.4)",
  "rgba(255, 103, 2, 0.65)",
  "rgba(255, 103, 2, 0.95)",
];

const HEATMAP_ROW_COUNT = HEATMAP_DATA.length;
const HEATMAP_COL_COUNT = HEATMAP_DATA[0]?.length ?? 0;

function heatmapCellDate(rowIdx, colIdx) {
  const cellIndex = colIdx * HEATMAP_ROW_COUNT + rowIdx;
  const daysAgo = HEATMAP_ROW_COUNT * HEATMAP_COL_COUNT - 1 - cellIndex;
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

function formatHeatmapDate(isoDate) {
  const date = new Date(`${isoDate}T00:00:00`);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function getHeatmapLevel(views, maxViews) {
  if (!views || views === 0 || maxViews === 0) return 0;
  const ratio = views / maxViews;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

function HeatmapGrid() {
  const containerRef = useRef(null);
  const [animated, setAnimated] = useState(false);
  const [hover, setHover] = useState(null);

  const { maxViews, totalViews } = useMemo(() => {
    let max = 0;
    let total = 0;
    for (const row of HEATMAP_DATA) {
      for (const views of row) {
        if (views > max) max = views;
        total += views;
      }
    }
    return { maxViews: max, totalViews: total };
  }, []);

  useEffect(() => {
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, []);

  return (
    <div className={styles.heatmapWrap} ref={containerRef}>
      <div className={styles.heatmapHeader}>
        <span className={styles.heatmapTitle}>
          {totalViews.toLocaleString()} views
        </span>
        <span className={styles.heatmapMeta}>Last 14 days</span>
      </div>

      <div className={styles.heatmapGrid}>
        {HEATMAP_DATA.map((row, rowIdx) => (
          <div key={rowIdx} className={styles.heatmapRow}>
            <span className={styles.heatmapDayLabel}>{HEATMAP_DAY_LABELS[rowIdx]}</span>
            <div className={styles.heatmapCells}>
              {row.map((views, colIdx) => {
                const isoDate = heatmapCellDate(rowIdx, colIdx);
                const level = getHeatmapLevel(views, maxViews);
                const isHovered =
                  hover?.rowIdx === rowIdx && hover?.colIdx === colIdx;
                return (
                  <span
                    key={colIdx}
                    role="presentation"
                    className={`${styles.heatmapCell} ${animated ? styles.heatmapCellVisible : ""} ${isHovered ? styles.heatmapCellHovered : ""}`}
                    style={{
                      backgroundColor: HEATMAP_COLORS[level],
                      transitionDelay: `${colIdx * 28 + rowIdx * 18}ms`,
                    }}
                    aria-label={`${views} ${views === 1 ? "view" : "views"} on ${formatHeatmapDate(isoDate)}`}
                    onMouseEnter={(event) => {
                      const containerRect = containerRef.current?.getBoundingClientRect();
                      const cellRect = event.currentTarget.getBoundingClientRect();
                      if (!containerRect) return;
                      setHover({
                        rowIdx,
                        colIdx,
                        views,
                        date: isoDate,
                        x: cellRect.left - containerRect.left + cellRect.width / 2,
                        y: cellRect.top - containerRect.top,
                      });
                    }}
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.heatmapLegend}>
        <span className={styles.heatmapLegendLabel}>Less</span>
        {HEATMAP_COLORS.map((color, index) => (
          <span
            key={index}
            className={styles.heatmapLegendCell}
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
        ))}
        <span className={styles.heatmapLegendLabel}>More</span>
      </div>

      {hover && (
        <div
          className={styles.heatmapTooltip}
          style={{
            left: hover.x,
            top: hover.y - 32,
            transform: "translateX(-50%)",
          }}
        >
          <span className={styles.heatmapTooltipCount}>
            {hover.views.toLocaleString()}
          </span>
          <span className={styles.heatmapTooltipMeta}>
            {hover.views === 1 ? "view" : "views"} · {formatHeatmapDate(hover.date)}
          </span>
        </div>
      )}
    </div>
  );
}

function EngagementCurve() {
  const [animated, setAnimated] = useState(false);

  // Kick off the draw-in animation just after mount. The `key` prop on the
  // parent <EngagementCurve> remounts this component each time the user
  // switches back to the engagement tab, so the animation replays.
  useEffect(() => {
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, []);

  const svgWidth = 600;
  const svgHeight = 340;
  const topPadding = 24;
  const bottomPadding = 80;
  const leftPadding = 0;
  const rightPadding = 0;

  const { linePath, areaPath } = useMemo(
    () =>
      buildEngagementPaths({
        counts: ENGAGEMENT_COUNTS,
        svgWidth,
        svgHeight,
        leftPadding,
        rightPadding,
        topPadding,
        bottomPadding,
      }),
    []
  );

  return (
    <div className={styles.engagementWrap}>
      <svg
        className={styles.engagementSvg}
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="engagementCurveGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff6702" stopOpacity="0.22" />
            <stop offset="60%" stopColor="#ff6702" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#ff6702" stopOpacity="0" />
          </linearGradient>
          <mask id="engagementDrawMask">
            <rect
              x={leftPadding}
              y={topPadding}
              width={svgWidth - leftPadding - rightPadding}
              height={svgHeight - bottomPadding - topPadding}
              fill="#fff"
              style={{
                transform: animated ? "scaleX(1)" : "scaleX(0)",
                transformOrigin: "left center",
                transformBox: "fill-box",
                transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            />
          </mask>
        </defs>

        <g mask="url(#engagementDrawMask)">
          <path d={areaPath} fill="url(#engagementCurveGradient)" stroke="none" />
        </g>
        <path
          d={linePath}
          pathLength="1"
          fill="none"
          stroke="#ff6702"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 1,
            strokeDashoffset: animated ? 0 : 1,
            transition: "stroke-dashoffset 1100ms cubic-bezier(0.22, 1, 0.36, 1)",
            filter: "drop-shadow(0 0 10px rgba(255, 103, 2, 0.35))",
          }}
        />
      </svg>

      <div
        className={`${styles.percentDisplay} ${animated ? styles.percentDisplayVisible : ""}`}
      >
        <span className={styles.percentLabel}>Average engagement</span>
        <span className={styles.percentValue}>
          {ENGAGEMENT_AVERAGE}
          <span className={styles.percentSign}>%</span>
        </span>
      </div>
    </div>
  );
}

export default function AnalyticsScrollingSection() {
  const [active, setActive] = useState(FEATURES[0].id);
  const [offset, setOffset] = useState(0);
  const activeFeature = FEATURES.find((f) => f.id === active) ?? FEATURES[0];
  const activeIndex = FEATURES.findIndex((f) => f.id === active);

  const listRef = useRef(null);
  const vizRef = useRef(null);
  const itemRefs = useRef({});

  // Translate the (sticky) viz so it visually aligns with the active item.
  // First item → viz top aligns with list top. Last item → viz bottom aligns
  // with last item's bottom. Middle items → proportional offset.
  useLayoutEffect(() => {
    const compute = () => {
      const list = listRef.current;
      const viz = vizRef.current;
      const item = itemRefs.current[active];
      if (!list || !viz || !item) return;

      if (window.matchMedia("(max-width: 900px)").matches) {
        setOffset(0);
        return;
      }

      const listRect = list.getBoundingClientRect();
      const vizHeight = viz.getBoundingClientRect().height;
      const itemRect = item.getBoundingClientRect();

      const itemTop = itemRect.top - listRect.top;
      const itemBottom = itemTop + itemRect.height;

      const isFirst = activeIndex === 0;
      const isLast = activeIndex === FEATURES.length - 1;

      let next;
      if (isFirst) {
        next = 0;
      } else if (isLast) {
        next = itemBottom - vizHeight;
      } else {
        const fraction = activeIndex / (FEATURES.length - 1);
        next = fraction * (listRect.height - vizHeight);
      }

      const max = Math.max(0, listRect.height - vizHeight);
      setOffset(Math.min(Math.max(next, 0), max));
    };

    compute();

    const ro = new ResizeObserver(compute);
    if (listRef.current) ro.observe(listRef.current);
    if (vizRef.current) ro.observe(vizRef.current);
    window.addEventListener("resize", compute);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [active, activeIndex]);

  // On mobile the feature list scrolls horizontally. Auto-select whichever
  // card is closest to the list's horizontal center as the user scrolls, so
  // the visualization above switches automatically. No-op on desktop.
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 900px)");
    let raf = 0;

    const selectCentered = () => {
      raf = 0;
      const list = listRef.current;
      if (!list || !mql.matches) return;

      const listRect = list.getBoundingClientRect();
      const center = listRect.left + listRect.width / 2;

      let bestId = null;
      let bestDist = Infinity;
      for (const f of FEATURES) {
        const el = itemRefs.current[f.id];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = f.id;
        }
      }

      if (bestId) setActive((prev) => (prev === bestId ? prev : bestId));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(selectCentered);
    };

    const sync = () => {
      const list = listRef.current;
      if (!list) return;
      list.removeEventListener("scroll", onScroll);
      if (mql.matches) {
        list.addEventListener("scroll", onScroll, { passive: true });
        selectCentered();
      }
    };

    sync();
    mql.addEventListener("change", sync);

    return () => {
      mql.removeEventListener("change", sync);
      if (listRef.current) listRef.current.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>All the data you need <br className={styles.mobileBreak} /> to make smart decisions</h2>
        </div>

        <div className={styles.layout}>
          <ul className={styles.featuresList} role="tablist" ref={listRef}>
            {FEATURES.map((f) => {
              const isActive = f.id === active;
              return (
                <li
                  key={f.id}
                  className={styles.featureItem}
                  ref={(el) => {
                    if (el) itemRefs.current[f.id] = el;
                    else delete itemRefs.current[f.id];
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`${styles.featureButton} ${isActive ? styles.featureButtonActive : ""}`}
                    onClick={() => setActive(f.id)}
                  >
                    <span className={styles.featureBody}>
                      <span className={styles.featureTitle}>{f.title}</span>
                      <span className={styles.featureDescription}>{f.description}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className={styles.vizCol}>
            <div
              ref={vizRef}
              className={styles.vizScreen}
              data-viz={active}
              style={{ transform: `translateY(${offset}px)` }}
            >
              {active === "engagement" ? (
                <EngagementCurve key={active} />
              ) : active === "geography" ? (
                <GeographyList key={active} />
              ) : active === "overview" ? (
                <HeatmapGrid key={active} />
              ) : active === "sessions" ? (
                <SessionActivityList key={active} />
              ) : (
                <div className={styles.placeholderLabel}>
                  <span className={styles.placeholderBadge}>Placeholder</span>
                  <span>Analytics visualization for</span>
                  <span className={styles.placeholderActiveTitle}>{activeFeature.title}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
